/**
 * Study Resource Enquiry Level Issue Service
 * Handles API calls for enquiry level issuing
 */

import Config from '../_config/config';

export interface EnquiryLevelIssue {
  Id: number;
  ResourceId: number;
  SchoolId?: number;
  SchoolTagId?: number;
  CourseId?: number;
  DeliveryFormatId?: number;
  YearId?: number;
  ProgramId?: number;
  StudentTagId?: number;
  StartDate?: string;
  EndDate?: string;
  CreatedByUserId?: number;
  CreationDate?: string;
}

export interface CreateEnquiryLevelIssueRequest {
  resourceId: number;
  type: "school" | "tag";
  schoolId?: number; // Can be SchoolId or TagId based on type
  courseId?: number;
  deliveryFormatId?: number;
  yearId?: number;
  programId?: number;
  studentTagId?: number;
  startDate?: string;
  endDate?: string;
}

class StudyResourceEnquiryIssueService {
  /**
   * Get authorization headers with JWT token
   */
  private getAuthHeaders(): HeadersInit {
    let token: string | null = null;
    const currentUser = localStorage.getItem('currentUser');
    if (currentUser && currentUser !== 'undefined') {
      try {
        token = JSON.parse(currentUser).token;
      } catch (e) {
        console.error('Failed to parse currentUser:', e);
      }
    }

    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  /**
   * Get all enquiry level issues for a resource
   */
  async getEnquiryLevelIssues(resourceId: number): Promise<EnquiryLevelIssue[]> {
    const response = await fetch(
      `${Config.nodeSiteUrl}v1/study-resource/enquiry-issue/${resourceId}`,
      {
        method: 'GET',
        headers: this.getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch enquiry level issues: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data || [];
  }

  /**
   * Create enquiry level issue
   */
  async createEnquiryLevelIssue(
    issue: CreateEnquiryLevelIssueRequest
  ): Promise<{ success: boolean; id: number; message: string }> {
    const response = await fetch(
      `${Config.nodeSiteUrl}v1/study-resource/enquiry-issue`,
      {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(issue),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `Failed to create enquiry level issue: ${response.statusText}`);
    }

    return data;
  }

  /**
   * Update enquiry level issue
   */
  async updateEnquiryLevelIssue(
    id: number,
    issue: Partial<CreateEnquiryLevelIssueRequest>
  ): Promise<{ success: boolean; message: string }> {
    const response = await fetch(
      `${Config.nodeSiteUrl}v1/study-resource/enquiry-issue/${id}`,
      {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(issue),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `Failed to update enquiry level issue: ${response.statusText}`);
    }

    return data;
  }

  /**
   * Delete enquiry level issue
   */
  async deleteEnquiryLevelIssue(id: number): Promise<{ success: boolean; message: string }> {
    const response = await fetch(
      `${Config.nodeSiteUrl}v1/study-resource/enquiry-issue/${id}`,
      {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `Failed to delete enquiry level issue: ${response.statusText}`);
    }

    return data;
  }
}

// Export singleton instance
export const studyResourceEnquiryIssueService = new StudyResourceEnquiryIssueService();
export default studyResourceEnquiryIssueService;
